import classNames from 'classnames';
import {observer, Provider} from 'mobx-react';
import React, {useRef} from 'react';

import {BrowseCourse as Course, BrowseCourseCard} from '@udemy/browse-course';
import {PAGE_TYPE_CATEGORY} from '@udemy/discovery-api';
import {ClickEvent, Tracker, TrackingContextProvider} from '@udemy/event-tracking';
import {useI18n} from '@udemy/i18n';

import {DiscoveryListContainer} from 'src/features/category/components/browse/discovery-list-container/discovery-list-container.react-component';
import {FreeCategoryProps} from 'src/features/category/types';

import {CommunitySection} from './components/community-section.react-component';
import {HeroFRC} from './components/hero-frc.react-component';
import {SubcategoriesSection} from './components/subcategories-section.react-component';
import {UFBSection} from './components/ufb-section.react-component';

const AGGREGATION_TYPE_PRICE = 'price';

export const FreeCategory = observer(
    ({freeCategoryConfig, categoryUrl, pageObject, subcategories}: FreeCategoryProps) => {
        const {gettext, interpolate} = useI18n();
        const scrollTargetRef = useRef<HTMLDivElement>(null);

        const handlePageChange = (componentName?: string) => () => {
            if (componentName) {
                Tracker.publishEvent(new ClickEvent({componentName}));
            }
            scrollTargetRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'});
        };

        const presetFilters = {[AGGREGATION_TYPE_PRICE]: ['price-free']};

        const renderCourses = (courses: Course[]) =>
            courses.map((course, index) => (
                <TrackingContextProvider key={course.id} trackingContext={{index}}>
                    <BrowseCourseCard course={course} size="large" renderPriceText={() => null} />
                </TrackingContextProvider>
            ));

        const courseDirectoryProps = {
            hiddenFilters: ['price', 'features', 'ratings', 'duration', 'closed_captions'],
            hideNumFiltersApplied: true,
            renderCourseCard: renderCourses,
            hiddenSortOptions: ['price-low-to-high', 'price-high-to-low'],
        };

        const renderStaticContent = () => {
            if (pageObject.title_cleaned === freeCategoryConfig.free.title_cleaned) {
                return (
                    <>
                        <div className="container--hero">
                            <div className="ud-container">
                                <HeroFRC jumpLinkHandler={handlePageChange('HeroFRC')} />
                            </div>
                        </div>
                        <div className="ud-container">
                            <SubcategoriesSection
                                subcategories={subcategories ?? []}
                                jumpLinkHandler={handlePageChange('SubcategoriesSection')}
                            />
                            <UFBSection />
                            <CommunitySection />
                        </div>
                    </>
                );
            }
            return null;
        };

        const renderTitle = () => {
            if (pageObject.title_cleaned === freeCategoryConfig.free.title_cleaned) {
                return (
                    <div ref={scrollTargetRef} data-purpose="category-header">
                        <h2 className="ud-heading-serif-xxl text-margin">
                            {gettext('Browse free courses')}
                        </h2>
                    </div>
                );
            }

            if (
                freeCategoryConfig.free.subcategories
                    .map((sc) => sc.title_cleaned)
                    .includes(pageObject.title_cleaned)
            ) {
                return (
                    <div
                        className="text-top-margin"
                        data-purpose="subcategory-header"
                        ref={scrollTargetRef}
                    >
                        <h1 className="text-margin">
                            <a className="ud-heading-xxl" href={categoryUrl}>
                                {pageObject.title}
                            </a>
                        </h1>
                        <h2 className="ud-heading-xl text-margin">
                            {interpolate(
                                gettext('Free %(subcategory)s courses'),
                                {subcategory: pageObject.title},
                                true,
                            )}
                        </h2>
                    </div>
                );
            }

            return null;
        };

        const categoryWrapperStyle = classNames({
            'category-page-wrapper': pageObject.type === PAGE_TYPE_CATEGORY,
        });

        return (
            <>
                {renderStaticContent()}
                <div className={categoryWrapperStyle}>
                    <div className="ud-container">
                        {renderTitle()}
                        {typeof window !== 'undefined' && (
                            <Provider isConsumerSubsSubscriber={false}>
                                <DiscoveryListContainer
                                    pageType={pageObject.type}
                                    pageSize={16}
                                    pageObjectId={Number(pageObject?.id)}
                                    presetFilters={presetFilters}
                                    courseCardSize="small"
                                    onPageChange={handlePageChange}
                                    filterOrder={['topic', 'language']}
                                    courseDirectoryProps={courseDirectoryProps}
                                    query={pageObject.title}
                                />
                            </Provider>
                        )}
                    </div>
                </div>
            </>
        );
    },
);
